import { AuthError } from '@supabase/supabase-js'

export interface AuthErrorInfo {
  message: string
  action?: AuthErrorAction
  code?: string
}

export type AuthErrorAction =
  | 'retry'
  | 'check_email'
  | 'sign_in'
  | 'contact_support'
  | 'stronger_password'
  | 'try_alternative'
  | 'check_connection'
  | 'wait'
  | 'fix_format'
  | 'enter_phone'
  | 'use_different'
  | 'retry_code'
  | 'request_new'
  | 'check_phone'
  | 'retry_later'

interface MessageShape {
  message?: unknown
  error?: unknown
}

function extractMessage(error: unknown): string {
  if (typeof error === 'string') return error
  if (error && typeof error === 'object') {
    const shape = error as MessageShape
    if (typeof shape.message === 'string') return shape.message
    if (typeof shape.error === 'string') return shape.error
  }
  return ''
}

function isAuthError(error: unknown): error is AuthError {
  return error instanceof Error && typeof (error as AuthError).message === 'string'
}

export function parseAuthError(error: unknown): AuthErrorInfo {
  if (!error) {
    return { message: 'An unexpected error occurred. Please try again.', action: 'retry' }
  }

  const rawMessage = extractMessage(error)
  const message = rawMessage.toLowerCase()

  if (!message) {
    return { message: 'An unexpected error occurred. Please try again.', action: 'retry' }
  }

  if (message.includes('invalid login credentials')) {
    return {
      message: 'Invalid email or password. Please check your credentials and try again.',
      action: 'retry',
      code: 'invalid_credentials'
    }
  }

  if (message.includes('email not confirmed')) {
    return {
      message: 'Please check your email and click the confirmation link before signing in.',
      action: 'check_email',
      code: 'email_not_confirmed'
    }
  }

  if (message.includes('user already registered')) {
    return {
      message: 'An account with this email already exists. Please sign in instead.',
      action: 'sign_in',
      code: 'user_exists'
    }
  }

  if (message.includes('signup disabled')) {
    return {
      message: 'New account registration is currently disabled. Please contact support.',
      action: 'contact_support',
      code: 'signup_disabled'
    }
  }

  if (message.includes('password') && message.includes('weak')) {
    return {
      message:
        'Password is too weak. Please use at least 8 characters with a mix of letters, numbers, and symbols.',
      action: 'stronger_password',
      code: 'weak_password'
    }
  }

  if (message.includes('oauth')) {
    return {
      message: 'Sign-in with Google failed. Please try again or use email/password.',
      action: 'try_alternative',
      code: 'oauth_error'
    }
  }

  if (message.includes('network') || message.includes('fetch')) {
    return {
      message: 'Network error. Please check your connection and try again.',
      action: 'check_connection',
      code: 'network_error'
    }
  }

  if (message.includes('rate limit') || message.includes('too many')) {
    return {
      message: 'Too many attempts. Please wait a few minutes before trying again.',
      action: 'wait',
      code: 'rate_limited'
    }
  }

  return {
    message: rawMessage,
    action: 'retry',
    code: isAuthError(error) ? 'supabase_error' : 'unknown_error'
  }
}

export function parsePhoneVerificationError(error: unknown): AuthErrorInfo {
  const rawMessage = extractMessage(error)
  const message = rawMessage.toLowerCase()

  if (!message) {
    return { message: 'Phone verification failed. Please try again.', action: 'retry' }
  }

  if (message.includes('invalid phone number') || message.includes('phone number format')) {
    return {
      message: 'Invalid phone number format. Please include country code (e.g., +1234567890).',
      action: 'fix_format',
      code: 'invalid_phone_format'
    }
  }

  if (message.includes('phone number is required')) {
    return {
      message: 'Phone number is required. Please enter your phone number.',
      action: 'enter_phone',
      code: 'phone_required'
    }
  }

  if (
    message.includes('already registered') ||
    message.includes('already used') ||
    (message.includes('phone') && (message.includes('already') || message.includes('exists')))
  ) {
    return {
      message: 'This phone number is already registered to another account.',
      action: 'use_different',
      code: 'phone_exists'
    }
  }

  if (message.includes('invalid verification code') || message.includes('invalid otp')) {
    return {
      message: 'Invalid verification code. Please check the code and try again.',
      action: 'retry_code',
      code: 'invalid_otp'
    }
  }

  if (message.includes('expired')) {
    return {
      message: 'Verification code has expired. Please request a new code.',
      action: 'request_new',
      code: 'otp_expired'
    }
  }

  if (message.includes('too many attempts') || message.includes('attempt')) {
    return {
      message: 'Too many verification attempts. Please request a new code.',
      action: 'request_new',
      code: 'too_many_attempts'
    }
  }

  if (message.includes('too many otp requests') || message.includes('wait')) {
    return {
      message: 'Too many verification requests. Please wait 10 minutes before requesting again.',
      action: 'wait',
      code: 'rate_limited'
    }
  }

  if (message.includes('otp')) {
    return {
      message: 'Invalid verification code. Please check the code and try again.',
      action: 'retry_code',
      code: 'invalid_otp'
    }
  }

  if (message.includes('failed to send') || message.includes('sms')) {
    return {
      message: 'Failed to send verification code. Please check your phone number and try again.',
      action: 'check_phone',
      code: 'sms_failed'
    }
  }

  if (message.includes('internal server error') || message.includes('network')) {
    return {
      message: 'Server error. Please try again in a few moments.',
      action: 'retry_later',
      code: 'server_error'
    }
  }

  if (message.includes('unauthorized')) {
    return {
      message: 'Session expired. Please sign in again.',
      action: 'sign_in',
      code: 'unauthorized'
    }
  }

  return {
    message: rawMessage || 'Phone verification failed. Please try again.',
    action: 'retry',
    code: 'verification_error'
  }
}

/**
 * Log an auth error with structured context. In production wire this to your
 * monitoring backend (Sentry, LogRocket, etc.); logs at `error` level here are
 * captured by whatever stdout collector the deployment uses.
 */
export function logAuthError(context: string, error: unknown, userId?: string) {
  // Provider messages and arbitrary payloads can contain identities, passwords,
  // or tokens. Keep only bounded diagnostic identifiers in shared auth logs.
  const diagnostics: { code?: string; status?: number } = {}
  if (error && typeof error === 'object') {
    if (
      'code' in error &&
      typeof error.code === 'string' &&
      /^[a-zA-Z0-9_]{1,64}$/.test(error.code)
    ) {
      diagnostics.code = error.code
    }
    if (
      'status' in error &&
      typeof error.status === 'number' &&
      Number.isInteger(error.status) &&
      error.status >= 100 &&
      error.status <= 599
    ) {
      diagnostics.status = error.status
    }
  }
  console.error('[Auth Error]', {
    context: /^[a-zA-Z0-9_ -]{1,80}$/.test(context) ? context : 'auth_failure',
    error: diagnostics,
    hasUserContext: Boolean(userId),
    timestamp: new Date().toISOString()
  })
}
